import type { HTMLAttributes, InputHTMLAttributes, LabelHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { inputClass } from './input-class';

export function Card({ className, ...p }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-[14px] border border-border-soft bg-panel', className)} {...p} />;
}

export function CardHeader({ title, description, action, className }: { title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between gap-3 px-5 pt-4 pb-3', className)}>
      <div className="min-w-0">
        <h3 className="text-[13px] font-medium text-fg">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-fg-3">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...p }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-5', className)} {...p} />;
}

const badgeTones = {
  neutral: 'bg-card text-fg-2 border-border',
  lime: 'bg-lime-soft text-lime border-lime/25',
  win: 'bg-win-soft text-win border-win/25',
  loss: 'bg-loss-soft text-loss border-loss/25',
  warn: 'bg-warn-soft text-warn border-warn/25',
  outline: 'bg-transparent text-fg-2 border-border',
} as const;

export function Badge({ tone = 'neutral', className, ...p }: HTMLAttributes<HTMLSpanElement> & { tone?: keyof typeof badgeTones }) {
  return (
    <span
      className={cn('inline-flex h-5 items-center gap-1 rounded-md border px-1.5 text-[11px] font-medium leading-none whitespace-nowrap [&_svg]:size-3', badgeTones[tone], className)}
      {...p}
    />
  );
}

export function Label({ className, ...p }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('mb-1.5 block text-xs font-medium text-fg-2', className)} {...p} />;
}

export { inputClass };

export function Input({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(inputClass, className)} {...p} />;
}

export function Textarea({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(inputClass, 'h-auto min-h-20 py-2', className)} {...p} />;
}

// The dropdown lives in its own client module (it needs state); re-exported so every screen keeps one import.
export { Select } from './select';

export function Field({ label, hint, error, children, className }: { label?: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      {children}
      {error ? <p className="mt-1 text-xs text-loss">{error}</p> : hint ? <p className="mt-1 text-xs text-fg-3">{hint}</p> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-card/70', className)} />;
}

export function Separator({ className }: { className?: string }) {
  return <div className={cn('h-px w-full bg-border-soft', className)} />;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="num rounded border border-border bg-bg-2 px-1.5 py-0.5 text-[10px] text-fg-3">{children}</kbd>;
}

export function EmptyState({ icon, title, description, action, className }: { icon?: ReactNode; title: string; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-10 text-center', className)}>
      {icon && <div className="mb-3 flex size-10 items-center justify-center rounded-xl border border-border bg-card text-fg-3 [&_svg]:size-5">{icon}</div>}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-sm text-xs leading-relaxed text-fg-3">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Progress({ value, tone = 'lime', className }: { value: number; tone?: 'lime' | 'win' | 'loss' | 'warn'; className?: string }) {
  const color = { lime: 'bg-lime', win: 'bg-win', loss: 'bg-loss', warn: 'bg-warn' }[tone];
  const track = { lime: 'bg-lime-soft', win: 'bg-win-soft', loss: 'bg-loss-soft', warn: 'bg-warn-soft' }[tone];
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full', track, className)}>
      <div className={cn('h-full rounded-full transition-[width] duration-300 ease-out', color)} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}
